import { parseUtcIso } from './parseUtcIso.js';

export const getDrawDateValue = (drawOrDate) => {
  if (!drawOrDate) return null;
  if (drawOrDate instanceof Date || typeof drawOrDate === 'string' || typeof drawOrDate === 'number') {
    return drawOrDate;
  }
  return drawOrDate.draw_date
    || drawOrDate.drawDate
    || drawOrDate.nextDrawDate
    || drawOrDate.next_draw_date
    || drawOrDate.next_elimination_date
    || drawOrDate.nextSessionDate
    || drawOrDate.next_session_date
    || drawOrDate.lastDate
    || drawOrDate.lastDrawDate
    || drawOrDate.last_draw_date
    || drawOrDate.drawDateTime
    || drawOrDate.date
    || null;
};

// Mapping from Windows/.NET timezone ids (returned by the API in `time_zone`)
// to IANA timezone identifiers usable with `Intl.DateTimeFormat`. Extend as
// needed when more orgs come online.
const WINDOWS_TZ_TO_IANA = {
  'Afghanistan Standard Time': 'Asia/Kabul',
  'Alaskan Standard Time': 'America/Anchorage',
  'Arab Standard Time': 'Asia/Riyadh',
  'Arabian Standard Time': 'Asia/Dubai',
  'Arabic Standard Time': 'Asia/Baghdad',
  'Argentina Standard Time': 'America/Argentina/Buenos_Aires',
  'Atlantic Standard Time': 'America/Halifax',
  'AUS Central Standard Time': 'Australia/Darwin',
  'AUS Eastern Standard Time': 'Australia/Sydney',
  'Azerbaijan Standard Time': 'Asia/Baku',
  'Azores Standard Time': 'Atlantic/Azores',
  'Canada Central Standard Time': 'America/Regina',
  'Cape Verde Standard Time': 'Atlantic/Cape_Verde',
  'Caucasus Standard Time': 'Asia/Yerevan',
  'Cen. Australia Standard Time': 'Australia/Adelaide',
  'Central America Standard Time': 'America/Guatemala',
  'Central Asia Standard Time': 'Asia/Almaty',
  'Central Brazilian Standard Time': 'America/Cuiaba',
  'Central Europe Standard Time': 'Europe/Budapest',
  'Central European Standard Time': 'Europe/Warsaw',
  'Central Pacific Standard Time': 'Pacific/Guadalcanal',
  'Eastern Standard Time': 'America/New_York',
  'Central Standard Time': 'America/Chicago',
  'Central Standard Time (Mexico)': 'America/Mexico_City',
  'China Standard Time': 'Asia/Shanghai',
  'Dateline Standard Time': 'Etc/GMT+12',
  'E. Africa Standard Time': 'Africa/Nairobi',
  'E. Australia Standard Time': 'Australia/Brisbane',
  'E. Europe Standard Time': 'Asia/Nicosia',
  'E. South America Standard Time': 'America/Sao_Paulo',
  'Eastern Standard Time (Mexico)': 'America/Cancun',
  'Egypt Standard Time': 'Africa/Cairo',
  'Ekaterinburg Standard Time': 'Asia/Yekaterinburg',
  'Fiji Standard Time': 'Pacific/Fiji',
  'FLE Standard Time': 'Europe/Kiev',
  'Georgian Standard Time': 'Asia/Tbilisi',
  'GMT Standard Time': 'Europe/London',
  'Greenland Standard Time': 'America/Godthab',
  'Greenwich Standard Time': 'Atlantic/Reykjavik',
  'GTB Standard Time': 'Europe/Istanbul',
  'Hawaiian Standard Time': 'Pacific/Honolulu',
  'India Standard Time': 'Asia/Calcutta',
  'Iran Standard Time': 'Asia/Tehran',
  'Israel Standard Time': 'Asia/Jerusalem',
  'Jordan Standard Time': 'Asia/Amman',
  'Korea Standard Time': 'Asia/Seoul',
  'Mauritius Standard Time': 'Indian/Mauritius',
  'Middle East Standard Time': 'Asia/Beirut',
  'Montevideo Standard Time': 'America/Montevideo',
  'Morocco Standard Time': 'Africa/Casablanca',
  'Mountain Standard Time': 'America/Denver',
  'Mountain Standard Time (Mexico)': 'America/Chihuahua',
  'Myanmar Standard Time': 'Asia/Rangoon',
  'N. Central Asia Standard Time': 'Asia/Novosibirsk',
  'Namibia Standard Time': 'Africa/Windhoek',
  'Nepal Standard Time': 'Asia/Katmandu',
  'New Zealand Standard Time': 'Pacific/Auckland',
  'Newfoundland Standard Time': 'America/St_Johns',
  'North Asia East Standard Time': 'Asia/Irkutsk',
  'North Asia Standard Time': 'Asia/Krasnoyarsk',
  'Pacific SA Standard Time': 'America/Santiago',
  'Pacific Standard Time': 'America/Los_Angeles',
  'Pacific Standard Time (Mexico)': 'America/Santa_Isabel',
  'Pakistan Standard Time': 'Asia/Karachi',
  'Paraguay Standard Time': 'America/Asuncion',
  'Romance Standard Time': 'Europe/Paris',
  'Russia Time Zone 11': 'Asia/Kamchatka',
  'Russia Time Zone 3': 'Europe/Samara',
  'Russian Standard Time': 'Europe/Moscow',
  'SA Eastern Standard Time': 'America/Cayenne',
  'SA Pacific Standard Time': 'America/Bogota',
  'SA Western Standard Time': 'America/La_Paz',
  'SE Asia Standard Time': 'Asia/Bangkok',
  'Singapore Standard Time': 'Asia/Singapore',
  'South Africa Standard Time': 'Africa/Johannesburg',
  'Sri Lanka Standard Time': 'Asia/Colombo',
  'Syria Standard Time': 'Asia/Damascus',
  'Taipei Standard Time': 'Asia/Taipei',
  'Tasmania Standard Time': 'Australia/Hobart',
  'Tokyo Standard Time': 'Asia/Tokyo',
  'Tonga Standard Time': 'Pacific/Tongatapu',
  'Turkey Standard Time': 'Europe/Istanbul',
  'US Eastern Standard Time': 'America/Indianapolis',
  'US Mountain Standard Time': 'America/Phoenix',
  'UTC': 'Etc/UTC',
  'UTC+12': 'Etc/GMT-12',
  'UTC-02': 'Etc/GMT+2',
  'UTC-11': 'Etc/GMT+11',
  'Ulaanbaatar Standard Time': 'Asia/Ulaanbaatar',
  'Venezuela Standard Time': 'America/Caracas',
  'Vladivostok Standard Time': 'Asia/Vladivostok',
  'W. Australia Standard Time': 'Australia/Perth',
  'W. Central Africa Standard Time': 'Africa/Lagos',
  'W. Europe Standard Time': 'Europe/Berlin',
  'West Asia Standard Time': 'Asia/Tashkent',
  'West Pacific Standard Time': 'Pacific/Port_Moresby',
  'Yakutsk Standard Time': 'Asia/Yakutsk',
};

export const getDrawIanaTimeZone = (drawOrDate) => {
  if (!drawOrDate || typeof drawOrDate !== 'object' || drawOrDate instanceof Date) return null;
  const tz = drawOrDate.time_zone || drawOrDate.timeZone;
  if (!tz) return null;
  if (WINDOWS_TZ_TO_IANA[tz]) return WINDOWS_TZ_TO_IANA[tz];
  // If it already looks like an IANA id (contains '/'), pass it through.
  if (/\//.test(tz)) return tz;
  return null;
};

const getDrawUtcOffset = (drawOrDate) => {
  if (!drawOrDate || typeof drawOrDate !== 'object' || drawOrDate instanceof Date) return null;
  const o = drawOrDate.utc_offset ?? drawOrDate.utcOffset ?? drawOrDate.UtcOffset;
  return typeof o === 'number' && !Number.isNaN(o) ? o : null;
};

const formatOffsetSuffix = (offsetHours) => {
  const sign = offsetHours >= 0 ? '+' : '-';
  const abs = Math.abs(offsetHours);
  const hh = String(Math.floor(abs)).padStart(2, '0');
  const mm = String(Math.round((abs % 1) * 60)).padStart(2, '0');
  return `${sign}${hh}:${mm}`;
};

export const parseDrawDate = (drawOrDate) => {
  const raw = getDrawDateValue(drawOrDate);
  if (!raw) return null;
  // When the caller passes a draw object with a known utc_offset, use it to
  // anchor naive ISO strings (the API returns local times like
  // `2026-04-27T20:35:40` paired with `utcOffset: -3`).
  const offset = getDrawUtcOffset(drawOrDate);
  if (offset != null && typeof raw === 'string') {
    const trimmed = raw.trim();
    if (trimmed && !/[zZ]|[+-]\d{2}:?\d{2}$/.test(trimmed)) {
      const d = new Date(`${trimmed}${formatOffsetSuffix(offset)}`);
      return isNaN(d.getTime()) ? null : d;
    }
  }
  return parseUtcIso(raw);
};

/**
 * Format a date string for display, respecting the user's locale.
 * Falls back to browser default locale when none specified.
 */
export const formatDrawDate = (dateString, locale) => {
  const date = parseDrawDate(dateString);
  if (!date) return '';
  return date.toLocaleDateString(locale || undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

export const formatDrawDateTime = (dateString, locale) => {
  const date = parseDrawDate(dateString);
  if (!date) return '';
  return date.toLocaleDateString(locale || undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

export const formatShortDate = (dateString, locale) => {
  const date = parseDrawDate(dateString);
  if (!date) return '';
  return date.toLocaleDateString(locale || undefined, {
    month: 'short',
    day: 'numeric'
  });
};

export const formatCompactDrawDate = (dateString, locale) => {
  const date = parseDrawDate(dateString);
  if (!date) return '';
  return date.toLocaleDateString(locale || undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

export const formatCompactDrawDateTime = (dateString, locale) => {
  const date = parseDrawDate(dateString);
  if (!date) return '';

  const resolvedLocale = locale || undefined;
  // Anchor the display to the draw's own timezone (when known) so the time
  // matches the Draw Date badge instead of rendering in the visitor's local
  // timezone. Include the zone abbreviation so the location is explicit.
  const ianaTz = getDrawIanaTimeZone(dateString);
  const dateOpts = {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  };
  const timeOpts = {
    hour: 'numeric',
    minute: '2-digit'
  };
  if (ianaTz) {
    dateOpts.timeZone = ianaTz;
    timeOpts.timeZone = ianaTz;
    timeOpts.timeZoneName = 'short';
  }
  const formattedDate = date.toLocaleDateString(resolvedLocale, dateOpts);
  const formattedTime = date.toLocaleTimeString(resolvedLocale, timeOpts);

  return `${formattedDate} \u00b7 ${formattedTime}`;
};

/**
 * Classify a bonus/additional draw's timing relative to the main draw.
 * 
 * @param {Object} draw - Additional draw object
 * @param {Object} [mainDraw] - Optional main draw object
 * @returns {Object} Timing classification { type, labelKey, defaultLabel, isEarlyBird, isConcurrent, drawDate, cutoffDate }
 */
export const classifyDrawTiming = (draw, mainDraw = null) => {
  if (!draw) {
    return {
      type: 'unknown',
      labelKey: 'bonusDraws.timingUnknown',
      defaultLabel: 'Bonus Draw',
      isEarlyBird: false,
      isConcurrent: false,
      drawDate: null,
      cutoffDate: null,
    };
  }

  const isExplicitEarlyBird = !!(draw.is_early_bird ?? draw.isEarlyBird);
  const drawDate = parseDrawDate(draw);
  const cutoffDate = parseDrawDate(draw.draw_cutoff_date ? { draw_date: draw.draw_cutoff_date, time_zone: draw.time_zone || draw.timeZone } : null);
  const mainDrawDate = mainDraw ? parseDrawDate(mainDraw) : null;

  // 1. Explicit Early Bird flag
  if (isExplicitEarlyBird) {
    return {
      type: 'early_bird',
      labelKey: 'bonusDraws.earlyBird',
      defaultLabel: 'Early Bird',
      isEarlyBird: true,
      isConcurrent: false,
      drawDate,
      cutoffDate,
    };
  }

  // 2. Comparison with main draw date when available
  if (drawDate && mainDrawDate) {
    const diffMs = drawDate.getTime() - mainDrawDate.getTime();
    // Within 2 minutes of main draw = concurrent / drawn at same time
    if (Math.abs(diffMs) <= 2 * 60 * 1000) {
      return {
        type: 'concurrent',
        labelKey: 'bonusDraws.drawnWithMain',
        defaultLabel: 'Drawn with Main Draw',
        isEarlyBird: false,
        isConcurrent: true,
        drawDate,
        cutoffDate,
      };
    }

    // Draw date strictly before main draw date
    if (diffMs < -2 * 60 * 1000) {
      return {
        type: 'early_bird',
        labelKey: 'bonusDraws.earlyBird',
        defaultLabel: 'Early Bird',
        isEarlyBird: true,
        isConcurrent: false,
        drawDate,
        cutoffDate,
      };
    }
  }

  // 3. Fallback: scheduled draw date
  if (drawDate) {
    return {
      type: 'scheduled',
      labelKey: 'bonusDraws.scheduled',
      defaultLabel: 'Bonus Draw',
      isEarlyBird: false,
      isConcurrent: false,
      drawDate,
      cutoffDate,
    };
  }

  return {
    type: 'bonus',
    labelKey: 'bonusDraws.bonusPrize',
    defaultLabel: 'Bonus Draw',
    isEarlyBird: false,
    isConcurrent: false,
    drawDate: null,
    cutoffDate: null,
  };
};
